import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AccordionDetails from '@material-ui/core/AccordionActions';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { navigate } from 'gatsby';
import {
  ViewBillDetails,
  AccountDetail,
  ViewBillMonthlyUsage,
  ViewBillAverageTemperature,
  QuickViewBillInfo,
  BillInfo,
  AutoPayInfo,
  PaymentPlanType,
  PgeEnergyTrackerData,
} from '../../__generated__/pge-types';
import PGEButton from '../buttons';
import colors from '../../themes/main-colors';
import { AccountParamsType } from '../../hooks/useViewBill';
import UsageGraph from './usage-graph';
import { useTranslation } from '../../hooks/useTranslation';
import Backdrop from '../../components/backdrop';
import ROUTES from '../../routes';
import {
  toDateString,
  toCurrencyString,
  toSentenceCase,
} from '../../util/format';

import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ViewRecentPayments from './view-recent-payments';
import SpinnerCard from '../spinner/SpinnerCard';
import EnergyUsage from '../account-summary/energy-usage';
import { CardContent } from '@material-ui/core';
import ErrorOverlay from '../error/error-overlay';
import IconMoney from '../../../src/static/MoneyIcon.svg';
import IconChartenergy from '../../../src/static/ChartenergyIcon.svg';
import CurrentBill from '../account-summary/current-bill';
import { TpaAccountDetails } from '../need-more-time-to-pay/tpa/useTPA';
import useAuth from '../../hooks/useAuth';
import TEST_IDS from '../../constants/test_ids';
import { useIsMobile } from '../../util/style-utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    colorTextPrimary: {
      color: colors.noirBlur,
    },
    rootNegativeMargin: {
      color: colors.noirBlur,
      paddingLeft: 56,
      paddingRight: 56,
      '& .MuiButton-endIcon': {
        marginRight: -280,
        marginLeft: 280,
      },
    },
    testButton: {
      color: colors.noirBlur,
      '&:hover': {
        color: '#FFF',
      },
      '& .MuiButton-label': {
        '& .MuiButton-endIcon': {
          marginLeft: '0px',
        },
      },
    },
    centerColumn: {
      width: '100%',
    },
    header: {
      margin: theme.spacing(1, 2, 2, 6),
      color: colors.noirBlur,
      fontWeight: 800,
    },
    accountCard: {
      margin: theme.spacing(2, 0, 0, 0),
      width: '100%',
    },
    text: {
      margin: theme.spacing(1, 2, 2, 2),
      color: colors.noirBlur,
    },
    textLeft: {
      margin: theme.spacing(1, 6, 2, 2),
      color: colors.noirBlur,
    },
    billText: {
      margin: theme.spacing(2, 0, 0, 0),
      color: colors.noirBlur,
    },
    billTextBottom: {
      margin: theme.spacing(2, 0, 2, 0),
      color: colors.noirBlur,
    },
    billTextMoney: {
      margin: theme.spacing(2, 0, 0, 0),
      fontWeight: 'bold',
    },
    buttonBox: {
      margin: theme.spacing(2, 0, 2, 0),
    },
    viewDetails: {
      margin: theme.spacing(0),
      color: colors.noirBlur,
      width: '100%',
    },
    hdivider: {
      margin: theme.spacing(1, 2, 0, 2),
    },
    hdivider2: {
      margin: theme.spacing(3, 1, 3, 1),
    },
    vdivider: {
      margin: theme.spacing(1, 0, 0, 0),
    },
    dolla: {
      margin: theme.spacing(2, 0, 2, 2),
      color: colors.noirBlur,
    },
    expander: {
      margin: theme.spacing(0, 0, 0, 4),
      color: colors.noirBlur,
    },
    viewDetailsGrid: {
      margin: theme.spacing(0, 10),
    },
    pdfBill: {
      margin: theme.spacing(4, 0, 0, 0),
    },
    buttonText: {
      color: colors.noirBlur,
    },
    graph: {
      margin: theme.spacing(2, 0, 0, 0),
    },
    comparisonTextLeft: {
      margin: theme.spacing(2, 2, 2, 6),
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(1),
        fontSize: '0.8rem',
      },
    },
    comparisonTextCenter: {
      margin: theme.spacing(2, 2, 2, 2),
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(1),
        fontSize: '0.8rem',
      },
    },
    comparisonTextRight: {
      margin: theme.spacing(2, 6, 2, 2),
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(1),
        fontSize: '0.8rem',
      },
    },
    navLinks: {
      margin: theme.spacing(2, 0, 0, 0),
      '& a': {
        textDecoration: 'none',
      },
    },
    noBills: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2),
    },
    noBillFound: {
      backgroundColor: colors.roseRed,
      padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
      display: 'flex',
      alignItems: 'center',
      borderRadius: 4,
    },
    link: {
      textDecoration: 'none',
    },
    billLinks: {
      display: 'flex',
      flexDirection: 'column',
    },
    amountOverDueAlert: {
      maxWidth: '300px',
      color: colors.errorRed,
      fontWeight: 600,
    },
    accordianTitle: {
      fontWeight: 'bold',
      marginBottom: '1em',
      color: colors.sparkBlue,
    },
  }),
);

type ViewBillProps = {
  accountParams: AccountParamsType | null | undefined;
  loading: boolean;
  billDetails: ViewBillDetails | null | undefined;
  selectedAccount: AccountDetail | null | undefined;
  monthUsage?: ViewBillMonthlyUsage | null | undefined;
  averageTemperature?: ViewBillAverageTemperature | null | undefined;
  quickSelectedAccount?: QuickViewBillInfo | null | undefined;
  latestPaymentHistory?: any;
  billInfo: BillInfo | null | undefined;
  autopay: AutoPayInfo | null | undefined;
  isPendingDisconnect: boolean | undefined;
  paymentPlanType: PaymentPlanType | null | undefined;
  encryptedAccountNumber: string | null | undefined;
  encryptedPersonId: string | undefined | null;
  tpaDetails?: TpaAccountDetails | undefined;
  pgeEnergyTracker?: PgeEnergyTrackerData | undefined | null;
  isSummaryBillAccount?: boolean | undefined;
};

const CommercialViewBill = ({
  accountParams,
  loading,
  billDetails,
  selectedAccount,
  monthUsage,
  averageTemperature,
  quickSelectedAccount,
  latestPaymentHistory,
  billInfo,
  autopay,
  isPendingDisconnect,
  paymentPlanType,
  encryptedAccountNumber,
  encryptedPersonId,
  tpaDetails,
  pgeEnergyTracker,
  isSummaryBillAccount,
}: ViewBillProps) => {
  const { isAuthenticated } = useAuth();
  const energyTrackerPanelRef = React.useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const accountNumber =
    accountParams?.accountNumber ||
    selectedAccount?.accountNumber ||
    quickSelectedAccount?.accountNumber;
  useEffect(() => {
    location.hash !== '#energyUse' && window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    //Focusing the energy tracker widget with panel open.
    if (
      !loading &&
      location.hash === '#energyTracker' &&
      typeof window !== 'undefined'
    ) {
      if (energyTrackerPanelRef.current) {
        energyTrackerPanelRef?.current?.click();
        window.scrollTo({
          top: energyTrackerPanelRef?.current?.getBoundingClientRect().top,
          behavior: 'smooth',
        });
      }
    }
  }, [loading]);

  const energyTrackerLoading = !pgeEnergyTracker;
  const pgeEnergyTrackerData = pgeEnergyTracker ? pgeEnergyTracker : undefined;
  const [isBillComparisionExpanded, setIsBillComparisionExpanded] = useState(
    true,
  );
  const showEnergyTrackerWidget =
    pgeEnergyTrackerData?.billableAccountDetails?.isAccountBillable &&
    pgeEnergyTrackerData?.billableAccountDetails
      ?.isServiceDesignationElectric &&
    pgeEnergyTrackerData?.energyTrackerInfo?.showEnergyTracker;

  const energyTrackerDataProps = {
    pgeEnergyTrackerData: pgeEnergyTrackerData,
    encryptedAccountNumber: encryptedAccountNumber,
    encryptedPersonId: encryptedPersonId,
    showHeader: false,
  };
  if (
    billDetails === undefined ||
    loading ||
    latestPaymentHistory === undefined
  ) {
    return <Backdrop forceOpen />;
  }

  return (
    <>
      <Grid className={classes.centerColumn}>
        <>
          <Paper
            className={classes.accountCard}
            data-testid={TEST_IDS.VIEW_BILL_CURRENT_BILL_CARD}
          >
            <Box justifyContent={'center'} display={'flex'}>
              <CurrentBill
                billInfo={billInfo || null}
                autoPay={autopay || null}
                paymentPlanType={paymentPlanType || null}
                isPendingDisconnect={isPendingDisconnect}
                billDetails={billDetails || null}
                tpaDetails={tpaDetails || undefined}
                isSummaryBillAccount={isSummaryBillAccount}
              />
            </Box>
          </Paper>

          <Paper
            className={classes.accountCard}
            data-testid={TEST_IDS.VIEW_BILL_BILL_COMPARISON_CARD}
          >
            <Accordion expanded={isBillComparisionExpanded}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                ref={energyTrackerPanelRef}
                onClick={() => {
                  setIsBillComparisionExpanded(!isBillComparisionExpanded);
                  if (!isAuthenticated && showEnergyTrackerWidget) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    navigate(ROUTES.SIGN_IN, {
                      state: {
                        redirectUrl:
                          ROUTES.VIEW_BILL +
                          '?selectedAccount=' +
                          accountNumber +
                          '#energyTracker',
                      },
                    });
                  }
                }}
              >
                <Grid
                  container
                  direction="row"
                  spacing={3}
                  style={isMobile ? { flexWrap: 'nowrap' } : {}}
                >
                  <Grid item>
                    <IconChartenergy />
                  </Grid>
                  <Grid item>
                    <Typography
                      className={classes.accordianTitle}
                      component={'span'}
                      variant={'h6'}
                    >
                      {t('BILL_COMPARISON_TITLE')}
                    </Typography>
                    <Typography variant={'body2'}>
                      {t('VIEW_BILL_ENERGY_CARD_SUBTITLE')}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                {showEnergyTrackerWidget &&
                  isAuthenticated &&
                  (energyTrackerLoading ? (
                    <Grid container>
                      <Grid item xs={12}>
                        <SpinnerCard />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid
                      item
                      xs={12}
                      style={{ padding: '0px' }}
                      data-testid="energy-card-with-energy-tracker"
                    >
                      <a id="energyUse" />
                      {energyTrackerDataProps?.pgeEnergyTrackerData ? (
                        <EnergyUsage {...energyTrackerDataProps} />
                      ) : (
                        <Paper>
                          {' '}
                          <CardContent>
                            {' '}
                            <ErrorOverlay
                              title={toSentenceCase(t('TRACK_YOUR_ENERGY_USE'))}
                            />{' '}
                          </CardContent>{' '}
                        </Paper>
                      )}
                    </Grid>
                  ))}
                {!showEnergyTrackerWidget && Boolean(monthUsage) ? (
                  <Grid container>
                    <Grid container>
                      <Grid
                        container
                        justify={'center'}
                        alignContent={'center'}
                      >
                        <Typography variant={'h2'} className={classes.billText}>
                          {t('ENERGY_USE')}
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        justify={'center'}
                        alignContent={'center'}
                        className={classes.graph}
                      >
                        <UsageGraph
                          monthUsage={monthUsage}
                          isMobile={isMobile}
                        />
                      </Grid>
                    </Grid>
                    <Divider />
                    <Grid container justify={'center'} alignContent={'center'}>
                      <Typography variant={'h2'} className={classes.billText}>
                        {t('COMPARISONS')}
                      </Typography>
                    </Grid>
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'baseline'}
                      style={{ width: '100%', padding: 10 }}
                    >
                      <Box>
                        <Typography
                          variant={'body1'}
                          style={{ fontWeight: 800 }}
                          className={classes.comparisonTextLeft}
                        >
                          {t('PERIOD_ENDING')}
                        </Typography>
                        <Typography
                          variant={'body1'}
                          className={classes.comparisonTextLeft}
                        >
                          {toDateString(
                            averageTemperature?.currentBillingPeriod?.date,
                            'MMM. yyyy',
                          )}
                        </Typography>
                        <Typography
                          variant={'body1'}
                          className={classes.comparisonTextLeft}
                        >
                          {toDateString(
                            averageTemperature?.previousBillingPeriod?.date,
                            'MMM. yyyy',
                          )}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant={'body1'}
                          style={{ fontWeight: 800 }}
                          className={classes.comparisonTextCenter}
                        >
                          {t('AVG_TEMP')}
                        </Typography>
                        {Boolean(
                          averageTemperature?.currentBillingPeriod
                            ?.averageTemperature,
                        ) ? (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            {Math.round(
                              averageTemperature?.currentBillingPeriod
                                ?.averageTemperature!,
                            )}
                            *
                          </Typography>
                        ) : (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            N/A
                          </Typography>
                        )}
                        {Boolean(
                          averageTemperature?.previousBillingPeriod
                            ?.averageTemperature,
                        ) ? (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            {Math.round(
                              averageTemperature?.previousBillingPeriod
                                ?.averageTemperature!,
                            )}
                            *
                          </Typography>
                        ) : (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            N/A
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography
                          variant={'body1'}
                          style={{ fontWeight: 800 }}
                          className={classes.comparisonTextCenter}
                        >
                          {t('MONTHLY_KWH')}
                        </Typography>
                        {Boolean(
                          averageTemperature?.currentBillingPeriod?.totalKwh,
                        ) ? (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            {averageTemperature?.currentBillingPeriod?.totalKwh}{' '}
                          </Typography>
                        ) : (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            N/A
                          </Typography>
                        )}
                        {Boolean(
                          averageTemperature?.previousBillingPeriod?.totalKwh,
                        ) ? (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            {
                              averageTemperature?.previousBillingPeriod
                                ?.totalKwh
                            }{' '}
                          </Typography>
                        ) : (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            N/A
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Typography
                          variant={'body1'}
                          style={{ fontWeight: 800 }}
                          className={classes.comparisonTextRight}
                        >
                          {t('MONTHLY_COST')}
                        </Typography>
                        {averageTemperature?.currentBillingPeriod?.totalCost ? (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            $
                            {toCurrencyString(
                              averageTemperature?.currentBillingPeriod
                                ?.totalCost || 0,
                              false,
                            )}{' '}
                          </Typography>
                        ) : (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            N/A
                          </Typography>
                        )}
                        {averageTemperature?.previousBillingPeriod
                          ?.totalCost ? (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            $
                            {toCurrencyString(
                              averageTemperature?.previousBillingPeriod
                                ?.totalCost || 0,
                              false,
                            )}{' '}
                          </Typography>
                        ) : (
                          <Typography
                            variant={'body1'}
                            className={classes.comparisonTextCenter}
                          >
                            N/A
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Typography
                      variant={'body1'}
                      className={classes.comparisonTextLeft}
                    >
                      {`*${t('TEMP_SOURCE')} ${
                        averageTemperature?.temperatureSource
                      }`}
                    </Typography>
                  </Grid>
                ) : null}
              </AccordionDetails>
            </Accordion>
          </Paper>

          <Paper
            className={classes.accountCard}
            data-testid="recent-payment-card"
          >
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Grid
                  container
                  direction="row"
                  spacing={3}
                  style={isMobile ? { flexWrap: 'nowrap' } : {}}
                >
                  <Grid item>
                    <IconMoney />
                  </Grid>
                  <Grid item>
                    <Typography
                      className={classes.accordianTitle}
                      component={'span'}
                      variant={'h6'}
                    >
                      {t('VIEW_BILL_RECENT_PAYMENTS_TITLE')}
                    </Typography>
                    <Typography variant={'body2'}>
                      {t('VIEW_BILL_RECENT_PAYMENTS_SUBTITLE')}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Grid item xs={12}>
                  <ViewRecentPayments
                    lastPaymentsDetails={latestPaymentHistory}
                  ></ViewRecentPayments>
                  <Divider />
                  <Box justifyContent={'center'} display={'flex'}>
                    <PGEButton
                      variant={'outlined'}
                      onClick={() => navigate(ROUTES.PAYMENT_HISTORY)}
                      style={{
                        width: '100%',
                        fontWeight: 400,
                        fontSize: 18,
                      }}
                      size={'large'}
                      className={classes.testButton}
                      endIcon={<ChevronRightIcon />}
                    >
                      {t('BILLING_PAYMENT')}
                    </PGEButton>
                  </Box>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </>
      </Grid>
    </>
  );
};

export default CommercialViewBill;
